import {union, mapValues, omitBy, isEmpty, omit} from 'lodash';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {constantIdentity, equals, assoc} from '@packages/utilities/fp/utils';
import {buildComponentInstancesGetter} from './buildComponentInstancesGetter';
import type {ComponentInstanceCounts} from '@packages/systems/core/records/DesignerState';
import {
  isUserComponentInst,
  getBody,
  getComponentSlots,
  getSlotOverrideElements,
  isUserComponent,
} from '@packages/utilities/platform';
import {Component, Expressions, matchExpression} from '@packages/systems/wfdl';
import {getName, getPluginName} from '@packages/systems/wfdl-Qualified';
import {computeComponentNestingMaps} from './componentNesting';
import {EElement, ExpressionElement} from '@packages/systems/wfdl-ast';

export {buildComponentInstancesGetter} from './buildComponentInstancesGetter';

export type ComponentCountByComponentId = {[componentId: string]: number};

export const componentInstCountForCurrentPage = weakMemo(
  (components: Component.ComponentMap): ComponentCountByComponentId => {
    const body = getBody(components);
    const counts: ComponentCountByComponentId = {};
    if (!body) {
      return counts;
    }

    Expressions.everything(constantIdentity)(
      matchExpression({
        Element: (element: ExpressionElement) => {
          if (!isUserComponentInst(components, element)) {
            return Expressions.resume(null);
          }

          // if a user component instance is found, increment the count
          const componentId = getName(element.type);
          incrementCount(counts, componentId, 1);
          const component = components.get(element.type);
          if (!component) {
            return Expressions.stop(null);
          }

          // continue traversal & counting only if the component has slots
          for (const slotElement of getComponentSlots(component)) {
            const overrideElements = getSlotOverrideElements(
              slotElement,
              element
            );
            if (overrideElements.length > 0) {
              return Expressions.resume(null);
            }
          }

          // otherwise stop counting because we are not considering nested components
          return Expressions.stop(null);
        },
        _: () => Expressions.resume(null),
      })
    )(EElement(body));
    return counts;
  }
);

/**
 * Returns a hash of components. The `key` is the componentId and the `value` is an object keyed by page and the value is the count on the page.
 */
export const componentInstanceCountsByPage = weakMemo(
  (liveComponentMap: Component.ComponentMap) =>
    weakMemo((componentInstanceCounts: ComponentInstanceCounts) =>
      singleMemo((pageId: string) => {
        const currentPageCompInstances = mapValues(
          componentInstCountForCurrentPage(liveComponentMap),
          (count) => ({[pageId]: count})
        );
        const counts = union(
          Object.keys(componentInstanceCounts),
          Object.keys(currentPageCompInstances)
        ).reduce<Record<string, any>>((memo, key) => {
          const value = omitBy(
            {
              ...componentInstanceCounts[key],
              ...currentPageCompInstances[key],
            },
            equals(0)
          );
          if (!isEmpty(value)) {
            memo[key] = value;
          }
          return memo;
        }, {});
        return counts;
      })
    )
);

/**
 * Given two sets of component instance counts (on the same page), calculate
 * the difference between the two sets.
 */
export const calculateComponentInstanceCountDifferences = ({
  countsBeforeChange,
  countsAfterChange,
}: {
  countsBeforeChange: ComponentCountByComponentId;
  countsAfterChange: ComponentCountByComponentId;
}) => {
  const changes = union(
    Object.keys(countsBeforeChange),
    Object.keys(countsAfterChange)
  ).reduce<ComponentCountByComponentId>((memo, componentId) => {
    const componentCountBefore = countsBeforeChange[componentId] || 0;
    const componentCountAfter = countsAfterChange[componentId] || 0;
    const change = componentCountAfter - componentCountBefore;
    if (change !== 0) {
      memo[componentId] = change;
    }
    return memo;
  }, {});
  if (!isEmpty(changes)) {
    return changes;
  }
  return null;
};

export const getAncestorComponents =
  (components: Component.ComponentMap) => (childComponentId: string) => {
    const getComponentInstances = buildComponentInstancesGetter(components);
    const nestedComponentTree: {[descendantComponentId: string]: string[]} = {};

    components.forEach((component, componentType) => {
      if (!isUserComponent(componentType)) return;
      // Library definitions can never contain user components (you can't
      // focus into library instances to edit them), so they never appear
      // in user component ancestor chains.
      if (Component.getOrigin(component)) return;

      const componentId = getName(componentType);
      const nestedComponentInstances = getComponentInstances(
        Component.getRender(component)
      );
      for (const nestedComponentInstance of nestedComponentInstances) {
        const nestedComponentId = getName(nestedComponentInstance.type);
        // if we already have a list of ids, push the current `componentId` on the end of the existing list
        if (nestedComponentId in nestedComponentTree) {
          nestedComponentTree[nestedComponentId]!.push(componentId);
        }
        // otherwise, create a list with the current `componentId`
        else {
          nestedComponentTree[nestedComponentId] = [componentId];
        }
      }
    });
    // Starting from the child component find all ancestral components
    const ancestors = new Set<string>();
    const queue: string[] = [childComponentId];
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) {
        continue;
      }
      for (const ancestor of nestedComponentTree[current] || []) {
        if (!ancestors.has(ancestor)) {
          ancestors.add(ancestor);
          queue.push(ancestor);
        }
      }
    }
    return Array.from(ancestors);
  };

export function hasRawComponentDefinitionReferences(
  components: Component.ComponentMap,
  componentId: string
): boolean {
  const {directParents, slotDefaultParents} =
    computeComponentNestingMaps(components);
  return (
    (directParents.get(componentId)?.size ?? 0) > 0 ||
    (slotDefaultParents.get(componentId)?.size ?? 0) > 0
  );
}

/**
 * Returns a hash of component symbols. The `key` is the symbolId and the `value` is the number of instances across the project.
 */
export const componentInstanceCountsSummary = weakMemo(
  (components: Component.ComponentMap) =>
    singleMemo((isComponentCanvasActive: boolean) =>
      weakMemo(
        (
          componentInstanceCounts: ComponentInstanceCounts
        ): ComponentCountByComponentId => {
          /*
            1. Initialize all component IDs with 0 so every component
               appears in the panel even when it has no instances.
          */
          const counts: ComponentCountByComponentId = {};
          components.forEach((_component, componentType) => {
            if (
              getPluginName(componentType) === '__SitePlugin' &&
              getName(componentType) !== 'page'
            ) {
              const componentId = getName(componentType);
              if (!counts[componentId]) {
                counts[componentId] = 0;
              }
            }
          });

          /*
            2. Count component usages from all other pages than the current page.

            We are assuming that the given `componentInstanceCounts` does not
            include instance counts from the current page.
          */
          for (const componentId in componentInstanceCounts) {
            const countsByPage = componentInstanceCounts[componentId];
            if (countsByPage) {
              for (const pageId in countsByPage) {
                const countOnPage = countsByPage[pageId];
                if (countOnPage) {
                  incrementCount(counts, componentId, countOnPage);
                }
              }
            }
          }

          /*
            3. Count component usages from the current page.

            Only do this if the Component Canvas is not active because instances on
            component pages do not count toward the totals.
          */
          if (!isComponentCanvasActive) {
            const currentPageCompInstances =
              componentInstCountForCurrentPage(components);
            for (const componentId in currentPageCompInstances) {
              const countOnCurrentPage = currentPageCompInstances[componentId];
              if (countOnCurrentPage) {
                incrementCount(counts, componentId, countOnCurrentPage);
              }
            }
          }

          /*
            4. Roll up nested component instances over one graph.

            Two edge kinds feed each component's total, both walked in a single
            Kahn topological pass so a parent's total is finalized before it
            propagates to its children:

              render nesting     child += parent's total × placements
                                 (each parent instance renders its own copy)
              slot defaults      child += placements when the parent is present
                                 (authored once on the definition, so
                                 placing the owner more times does not multiply)

            Cycles are not defensively handled here: both edge kinds are kept
            acyclic by validateNoCircularComponents, which checks render nesting
            through its element walk and slot defaults through its
            slotDefaultsHaveCycles branch.
          */
          const {
            directChildren,
            childPlacementCounts,
            slotDefaultPlacementCounts,
          } = computeComponentNestingMaps(components);

          const reachable = new Set<string>();
          for (const componentId in counts) {
            if (counts[componentId]! > 0) {
              reachable.add(componentId);
            }
          }
          // Mark all transitively reachable components across both edge kinds.
          const expansionQueue: string[] = [...reachable];
          let eHead = 0;
          while (eHead < expansionQueue.length) {
            const current = expansionQueue[eHead++]!;
            const renderChildren = directChildren.get(current);
            if (renderChildren) {
              for (const child of renderChildren) {
                if (!reachable.has(child)) {
                  reachable.add(child);
                  expansionQueue.push(child);
                }
              }
            }
            const slotChildren = slotDefaultPlacementCounts.get(current);
            if (slotChildren) {
              for (const child of slotChildren.keys()) {
                if (!reachable.has(child)) {
                  reachable.add(child);
                  expansionQueue.push(child);
                }
              }
            }
          }

          // Kahn's algorithm: in-degrees over both edge kinds, so a child is
          // only processed once all of its parents (render and slot) are final.
          const inDegree = new Map<string, number>();
          for (const id of reachable) {
            inDegree.set(id, 0);
          }
          const addInDegrees = (
            parentId: string,
            childIds: Iterable<string>
          ) => {
            if (!reachable.has(parentId)) return;
            for (const childId of childIds) {
              if (reachable.has(childId)) {
                inDegree.set(childId, (inDegree.get(childId) ?? 0) + 1);
              }
            }
          };
          for (const [parentId, children] of directChildren) {
            addInDegrees(parentId, children);
          }
          for (const [parentId, children] of slotDefaultPlacementCounts) {
            addInDegrees(parentId, children.keys());
          }

          const topoQueue: string[] = [];
          for (const [id, deg] of inDegree) {
            if (deg === 0) topoQueue.push(id);
          }

          const decrementInDegree = (childId: string) => {
            const newDeg = (inDegree.get(childId) ?? 1) - 1;
            inDegree.set(childId, newDeg);
            if (newDeg === 0) topoQueue.push(childId);
          };

          let tHead = 0;
          while (tHead < topoQueue.length) {
            const parentId = topoQueue[tHead++]!;
            const parentTotal = counts[parentId] ?? 0;

            const renderPlacements = childPlacementCounts.get(parentId);
            if (renderPlacements) {
              for (const [childId, placementCount] of renderPlacements) {
                if (!reachable.has(childId)) continue;
                incrementCount(counts, childId, parentTotal * placementCount);
                decrementInDegree(childId);
              }
            }

            const slotPlacements = slotDefaultPlacementCounts.get(parentId);
            if (slotPlacements) {
              for (const [childId, placementCount] of slotPlacements) {
                if (!reachable.has(childId)) continue;
                incrementCount(
                  counts,
                  childId,
                  parentTotal > 0 ? placementCount : 0
                );
                decrementInDegree(childId);
              }
            }
          }

          return counts;
        }
      )
    )
);

function incrementCount(
  counts: ComponentCountByComponentId,
  componentId: string,
  num: number
) {
  const c = counts[componentId] ?? 0;
  counts[componentId] = c + num;
}

/**
 * Calculate the component instance counts for all pages.
 * Optional {pageIdToExclude} arg can be provided if it is present the count will exclude that pageId.
 * pageIdToExclude arg should be provided when the current page can contain new or deleted instances.
 * Returns a hash of symbols. The `key` is the symbolId,
 * and the `value` a hash of {[pageId]: componentInstanceCount}.
 */
export const calculateComponentInstanceCountsForPages = (
  symbols: JSONNode[],
  opts?: {pageIdToExclude?: string}
) => {
  const {pageIdToExclude} = opts || {};
  const componentInstanceCounts = symbols.reduce(
    (acc: ComponentInstanceCounts, symbol) => {
      const copies = symbol?.data?.sym?.copies;
      if (copies && symbol?.data?.sym?.root) {
        // Remove the current page's instance counts (if provided)
        const value = pageIdToExclude ? omit(copies, pageIdToExclude) : copies;
        if (!isEmpty(value)) {
          acc[symbol._id] = value;
        }
      }
      return acc;
    },
    {}
  );

  return componentInstanceCounts;
};

/**
 * Update jsonNodes with component instance counts
 * in the `data.sym.copies` field
 */
export const updateWithComponentInstanceCounts = (
  symbols: JSONNode[],
  componentInstanceCounts: ComponentInstanceCounts
): JSONNode[] => {
  return symbols.map((symbol) => {
    const count = componentInstanceCounts[symbol._id];
    if (symbol?.data?.sym?.root) {
      // remove copies from the symbol if our instance doesn't exist in
      // the instance counts object. This means that there are no instances for
      // that component and our component should reflect that
      if (!count) {
        // eslint-disable-next-line webflow/no-delete
        delete symbol.data?.sym?.copies;
        return symbol;
      }

      const sym = assoc('copies')(count)(symbol.data?.sym);
      const data = assoc('sym')(sym)(symbol.data);
      return assoc('data')(data)(symbol);
    }
    return symbol;
  });
};

/**
 * Remove the instance counts for the given pageId from the component instance counts
 */
export const removePageFromComponentInstanceCounts = (
  componentInstanceCounts: ComponentInstanceCounts,
  pageIdToExclude: string
): ComponentInstanceCounts => {
  const instanceCountWithCurrentPageRemoved = Object.entries(
    componentInstanceCounts
  ).reduce((acc: ComponentInstanceCounts, [componentId, pageCounts]) => {
    const {[pageIdToExclude]: _, ...counts} = pageCounts;
    return {
      ...acc,
      [componentId]: counts,
    };
  }, {});

  return instanceCountWithCurrentPageRemoved;
};

export const mergeComponentInstanceCounts = (
  componentInstanceCounts_1: {[pageId: string]: number} | undefined,
  componentInstanceCounts_2: {[pageId: string]: number} | undefined
): {[pageId: string]: number} | undefined => {
  if (!componentInstanceCounts_1 && !componentInstanceCounts_2) {
    // Keep it undefined if both are undefined
    return undefined;
  }
  // Early return if only one is defined
  if (!componentInstanceCounts_1) {
    return componentInstanceCounts_2;
  }
  if (!componentInstanceCounts_2) {
    return componentInstanceCounts_1;
  }

  const newCount = {...componentInstanceCounts_1};

  Object.entries(componentInstanceCounts_2).forEach(([pageId, count]) => {
    if (newCount[pageId]) {
      newCount[pageId] += count;
    } else {
      newCount[pageId] = count;
    }
  });

  return newCount;
};

/**
 * Recalculate component instance counts for a specific page and merge those counts back
 * into our input components.
 *
 * Excludes the given page from the existing components copies, recounts
 * instances from the provided component map, and writes the result
 * back to the components. This makes the recalculation the sole source
 * of truth for that page's counts.
 *
 * @param components site components
 * @param pageComponentMap Component map for recalculation page
 * @param pageId page to recalculate counts for
 */
export const recalculateInstanceCountsForPage = (
  components: JSONNode[],
  pageComponentMap: Component.ComponentMap,
  pageId: string
): JSONNode[] => {
  const existingCounts = calculateComponentInstanceCountsForPages(components, {
    pageIdToExclude: pageId,
  });
  const updatedCounts =
    componentInstanceCountsByPage(pageComponentMap)(existingCounts)(pageId);
  return updateWithComponentInstanceCounts(components, updatedCounts);
};
